import { useEffect, useState, useCallback, useMemo } from 'react';
import { StoryCard } from './story-card';
import { Spinner } from './spinner';
import { useAuth } from '../systems/auth-context';
import { apiFetch } from '../systems/api-client';
import { refreshLocalCompletionsFromServer } from '../systems/mission-progress';
import { canAccessScenarioTools } from '../systems/roles';
import { useWorkspace } from '../systems/workspace-context';
import { resolveScenarioAssetUrl, type ScenarioAssetLayer } from '../systems/scenario-asset-url';
import { setScenarioReleaseToProd } from './scenario-editor/scenario-api';
import styles from './story-selection-screen.module.css';

interface LibraryListItem {
  id: string;
  scenarioId?: string;
  missionId?: string;
  title: string;
  scenarioTitle?: string;
  thumbnail: string;
  version?: string;
  missionIndex?: number;
  missionCount?: number;
  releaseToProd?: boolean;
  locked?: boolean;
}

interface ScenarioGroup {
  scenarioId: string;
  title: string;
  version?: string;
  releaseToProd: boolean;
  visible: number;
  total: number;
  missions: LibraryListItem[];
}

interface ReleaseConfirmState {
  scenarioId: string;
  title: string;
  releaseToProd: boolean;
}

export interface StorySelectionScreenProps {
  onSelectStory: (
    libraryItemId: string,
    missionId?: string,
    layer?: ScenarioAssetLayer,
    editorCatalogMode?: boolean
  ) => void;
  apiBaseUrl?: string;
  /** Increment to force-refresh the library (e.g. after returning from a mission). */
  refreshKey?: number;
  /** Shown only on workspaces with the leaderboard turned on. */
  onOpenLeaderboard?: () => void;
}

function resolveScenarioId(item: LibraryListItem): string {
  return item.scenarioId ?? item.id.split('::')[0] ?? item.id;
}

function groupMissionsByScenario(items: LibraryListItem[]): ScenarioGroup[] {
  const groups = new Map<string, LibraryListItem[]>();
  const order: string[] = [];

  for (const item of items) {
    const scenarioId = resolveScenarioId(item);
    if (!groups.has(scenarioId)) {
      groups.set(scenarioId, []);
      order.push(scenarioId);
    }
    groups.get(scenarioId)!.push(item);
  }

  return order.map((scenarioId) => {
    const missions = [...groups.get(scenarioId)!].sort(
      (a, b) => (a.missionIndex ?? 0) - (b.missionIndex ?? 0)
    );
    const first = missions[0]!;

    return {
      scenarioId,
      title: first.scenarioTitle ?? first.title,
      version: first.version,
      releaseToProd: first.releaseToProd === true,
      visible: missions.filter((m) => !m.locked).length,
      total: first.missionCount ?? missions.length,
      missions,
    };
  });
}

export function StorySelectionScreen({
  onSelectStory,
  apiBaseUrl = '/api',
  refreshKey = 0,
  onOpenLeaderboard,
}: StorySelectionScreenProps) {
  const { user } = useAuth();
  const { workspaceName, workspaceHeaders, isWorkspaceReady } = useWorkspace();
  const [scenarios, setScenarios] = useState<LibraryListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [catalogMode, setCatalogMode] = useState(false);
  const [catalogLayer, setCatalogLayer] = useState<ScenarioAssetLayer>('master');
  const [releaseConfirm, setReleaseConfirm] = useState<ReleaseConfirmState | null>(null);
  const [releasingScenarioId, setReleasingScenarioId] = useState<string | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const canUseCatalog = canAccessScenarioTools(user);

  const fetchScenarios = useCallback(async () => {
    if (!user || !isWorkspaceReady) return;

    setLoading(true);
    setError(null);

    try {
      await refreshLocalCompletionsFromServer();

      const params = new URLSearchParams();
      if (canUseCatalog && catalogMode) {
        params.set('all', 'true');
        params.set('layer', catalogLayer);
      }

      const query = params.toString();
      const url = `${apiBaseUrl}/scenarios${query ? `?${query}` : ''}`;
      const response = await apiFetch(url, {
        headers: {
          ...workspaceHeaders,
        },
      });

      if (response.status === 401) return;

      if (response.status === 403) {
        const data = await response.clone().json().catch(() => ({})) as { error?: string };
        if (data.error === 'Invalid or expired token') return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch scenarios: ${response.statusText}`);
      }

      const data = await response.json();
      setScenarios(data);
    } catch (err) {
      console.error('Error fetching scenarios:', err);
      setError(err instanceof Error ? err.message : 'Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, user, canUseCatalog, catalogMode, catalogLayer, isWorkspaceReady, workspaceHeaders]);

  useEffect(() => {
    if (user && isWorkspaceReady) {
      fetchScenarios();
    }
  }, [user, refreshKey, fetchScenarios, isWorkspaceReady]);

  const handleStoryClick = (libraryItemId: string, missionId?: string) => {
    const inEditorCatalog = canUseCatalog && catalogMode;
    const layer = inEditorCatalog ? catalogLayer : undefined;
    onSelectStory(libraryItemId, missionId, layer, inEditorCatalog);
  };

  const getThumbnailUrl = (item: LibraryListItem, thumbnail: string): string => {
    if (!thumbnail) return '';
    const scenarioId = resolveScenarioId(item);
    const layer: ScenarioAssetLayer =
      canUseCatalog && catalogMode && catalogLayer === 'draft' ? 'draft' : 'master';
    return (
      resolveScenarioAssetUrl(thumbnail, scenarioId, item.version || '0', { layer }) ?? ''
    );
  };

  const showCatalogMode = canUseCatalog && catalogMode;
  const showReleaseToggle = showCatalogMode && catalogLayer === 'master';
  const scenarioGroups = useMemo(() => groupMissionsByScenario(scenarios), [scenarios]);

  const handleReleaseCheckboxClick = (
    event: React.MouseEvent,
    group: ScenarioGroup
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (releasingScenarioId) return;

    setReleaseError(null);
    setReleaseConfirm({
      scenarioId: group.scenarioId,
      title: group.title,
      releaseToProd: !group.releaseToProd,
    });
  };

  const handleReleaseConfirm = async () => {
    if (!releaseConfirm || !user) return;

    const { scenarioId, releaseToProd } = releaseConfirm;
    setReleasingScenarioId(scenarioId);
    setReleaseError(null);

    try {
      await setScenarioReleaseToProd(scenarioId, releaseToProd);
      setScenarios((prev) =>
        prev.map((item) =>
          resolveScenarioId(item) === scenarioId
            ? { ...item, releaseToProd }
            : item
        )
      );
      setReleaseConfirm(null);
    } catch (err) {
      setReleaseError(err instanceof Error ? err.message : 'Échec de la mise à jour');
    } finally {
      setReleasingScenarioId(null);
    }
  };

  const renderStoryGrid = () => {
    if (loading) {
      return (
        <Spinner layout="centered" label="Chargement des histoires..." className={styles.loadingMessage} />
      );
    }

    if (error) {
      return (
        <div className={styles.errorMessage}>
          <p>Erreur : {error}</p>
          <button
            className={styles.retryButton}
            onClick={fetchScenarios}
            aria-label="Réessayer de charger les histoires"
          >
            Réessayer
          </button>
        </div>
      );
    }

    if (scenarios.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon} aria-hidden="true">📚</div>
          <div className={styles.emptyStateText}>Aucune histoire disponible</div>
          <div className={styles.emptyStateHint}>
            {showCatalogMode
              ? 'Aucun scénario trouvé pour cette couche.'
              : 'Ajoutez des scénarios à votre entreprise pour commencer.'}
          </div>
        </div>
      );
    }

    return (
      <main className={styles.storySections} aria-label="Histoires disponibles">
        {scenarioGroups.map((group) => {
          const sectionTitle = showCatalogMode && group.version
            ? `${group.title} (${group.version})`
            : group.title;
          const headingId = `scenario-heading-${group.scenarioId}`;
          const isReleasing = releasingScenarioId === group.scenarioId;

          return (
            <section
              key={group.scenarioId}
              className={styles.scenarioSection}
              aria-labelledby={headingId}
            >
              <header className={styles.scenarioSectionHeader}>
                <div className={styles.scenarioSectionHeaderRow}>
                  <div className={styles.scenarioTitleGroup}>
                    {showReleaseToggle && (
                      <label
                        className={styles.releaseToggle}
                        title={group.releaseToProd ? 'Visible pour les joueurs' : 'Masqué pour les joueurs'}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={group.releaseToProd}
                          disabled={isReleasing || releasingScenarioId !== null}
                          onClick={(e) => handleReleaseCheckboxClick(e, group)}
                          onChange={() => {}}
                          aria-label={
                            group.releaseToProd
                              ? `Retirer ${group.title} de la production`
                              : `Publier ${group.title} en production`
                          }
                        />
                      </label>
                    )}
                    <h2 id={headingId} className={styles.scenarioName}>
                      {sectionTitle}
                    </h2>
                  </div>
                  <span className={styles.scenarioCount} aria-label="Missions disponibles">
                    {group.visible} / {group.total}
                  </span>
                </div>
                <div className={styles.scenarioSectionDivider} aria-hidden="true" />
              </header>

              <div className={styles.storyGrid} role="list" aria-label={`Missions de ${sectionTitle}`}>
                {group.missions.map((mission) => (
                  <div key={mission.id} role="listitem" className={styles.storyGridItem}>
                    <StoryCard
                      id={mission.id}
                      title={mission.title}
                      thumbnail={getThumbnailUrl(mission, mission.thumbnail)}
                      locked={mission.locked === true && !showCatalogMode}
                      onClick={() => handleStoryClick(mission.id, mission.missionId)}
                    />
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </main>
    );
  };

  return (
    <div className={styles.selectionScreen}>
      <header className={styles.header}>
        <div className={styles.titleContainer}>
          <h1 className={styles.title}>{workspaceName || user?.enterprise_name || 'Histoires'}</h1>
          <p className={styles.subtitle}>Sélectionnez une mission pour commencer</p>
        </div>

        {onOpenLeaderboard && (
          <div className={styles.headerButtons}>
            <button type="button" className="btn-ghost" onClick={onOpenLeaderboard}>
              Classement
            </button>
          </div>
        )}

        {canUseCatalog && (
          <div className={styles.catalogFilters} role="group" aria-label="Filtres catalogue éditeur">
            <label className={styles.filterCheckbox}>
              <input
                type="checkbox"
                checked={catalogMode}
                onChange={(e) => setCatalogMode(e.target.checked)}
              />
              <span>Mode catalogue éditeur</span>
            </label>

            {catalogMode && (
              <div className={styles.layerRadios} role="radiogroup" aria-label="Couche du scénario">
                <label className={styles.filterRadio}>
                  <input
                    type="radio"
                    name="catalog-layer"
                    value="master"
                    checked={catalogLayer === 'master'}
                    onChange={() => setCatalogLayer('master')}
                  />
                  <span>Master</span>
                </label>
                <label className={styles.filterRadio}>
                  <input
                    type="radio"
                    name="catalog-layer"
                    value="draft"
                    checked={catalogLayer === 'draft'}
                    onChange={() => setCatalogLayer('draft')}
                  />
                  <span>Draft</span>
                </label>
              </div>
            )}
          </div>
        )}
      </header>

      {renderStoryGrid()}

      {releaseConfirm && (
        <div
          className={styles.modalOverlay}
          onClick={releasingScenarioId ? undefined : () => setReleaseConfirm(null)}
        >
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="release-confirm-title"
          >
            <header className={styles.modalHeader}>
              <h3 id="release-confirm-title">
                {releaseConfirm.releaseToProd ? 'Publier en production' : 'Retirer de la production'}
              </h3>
            </header>
            <div className={styles.modalBody}>
              <p>
                {releaseConfirm.releaseToProd
                  ? `Rendre « ${releaseConfirm.title} » visible pour les joueurs de ce workspace ? Ils verront la version master actuelle.`
                  : `Masquer « ${releaseConfirm.title} » pour les joueurs ? Seuls les éditeurs DarwinX continueront à le voir dans le catalogue.`}
              </p>
              {releaseError && (
                <p className={styles.modalError} role="alert">
                  {releaseError}
                </p>
              )}
            </div>
            <footer className={styles.modalFooter}>
              <button
                type="button"
                className={styles.modalBtnSecondary}
                onClick={() => setReleaseConfirm(null)}
                disabled={releasingScenarioId !== null}
              >
                Non
              </button>
              <button
                type="button"
                className={styles.modalBtnPrimary}
                onClick={handleReleaseConfirm}
                disabled={releasingScenarioId !== null}
              >
                {releasingScenarioId ? 'Enregistrement…' : 'Oui'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
